import { useState, useEffect, useRef } from "react";
import { Lock, Unlock, Ruler, CheckCircle2, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  computeRatio,
  deriveGarmentSku,
  deriveSizeRange,
  getAppliedGarments,
  areaCm2,
  suggestSizeTier,
  lookupSizing,
  confirmSizing,
} from "./printSizing";
import { ArtworkUpload } from "./ArtworkUpload";

/**
 * Print Sizing section for a Print Group.
 * Physical width/height + derived ratio + lock toggle + Confirm sizing (writes to reference store).
 * Zero impact on pricing — Size Tier is only *suggested*, never forced.
 */
export function PrintSizingSection({ artwork, index, garments, jobId, onUpdate, onUpdateFields }) {
  const [locked, setLocked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [autoNote, setAutoNote] = useState(null);
  const lockedRatioRef = useRef(null);
  const lastLookupKeyRef = useRef(null);

  const width = artwork.print_width_mm ?? "";
  const height = artwork.print_height_mm ?? "";
  const computedRatio = computeRatio(width, height);
  // Displayed ratio: an applied artwork-detected ratio takes precedence over the w/h-derived one
  const usingDetected = artwork.ratio_source === "detected" && artwork.ratio_decimal;
  const ratio = usingDetected
    ? { decimal: artwork.ratio_decimal, display: artwork.ratio_display }
    : computedRatio;
  const area = areaCm2(width, height);
  const suggestedTier = suggestSizeTier(width, height);

  const appliedGarments = getAppliedGarments(artwork, garments);
  // Reference key targets: one per applied garment line
  const keyTargets = appliedGarments.map((g) => ({
    sku: deriveGarmentSku(g),
    label: g.name || "Garment",
    sizeRange: deriveSizeRange(g),
    colour: g.colour,
  }));
  const primaryTarget = keyTargets[0] || null;

  // Persist derived ratio + size range whenever dimensions or applied garments change.
  // Skip the ratio overwrite when an artwork-detected ratio is applied (only refresh size range).
  useEffect(() => {
    const fields = { size_range: primaryTarget?.sizeRange || "" };
    if (!usingDetected) {
      fields.ratio_decimal = computedRatio.decimal;
      fields.ratio_display = computedRatio.display;
    }
    const changed =
      (!usingDetected &&
        (artwork.ratio_decimal !== fields.ratio_decimal ||
          artwork.ratio_display !== fields.ratio_display)) ||
      artwork.size_range !== fields.size_range;
    if (changed) {
      onUpdateFields(fields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, primaryTarget?.sizeRange, usingDetected]);

  // Auto-fill from reference store on exact-key match (only when width/height are empty)
  useEffect(() => {
    if (!primaryTarget?.sku || !artwork.placement_name) return;
    if (width || height) return;
    const key = `${primaryTarget.sku}|${primaryTarget.sizeRange}|${artwork.placement_name}`;
    if (lastLookupKeyRef.current === key) return;
    lastLookupKeyRef.current = key;
    let active = true;
    lookupSizing(primaryTarget.sku, artwork.placement_name, primaryTarget.sizeRange).then((res) => {
      if (!active || !res.found || !res.reference) return;
      const ref = res.reference;
      const r = computeRatio(ref.width_mm, ref.height_mm);
      onUpdateFields({
        print_width_mm: ref.width_mm,
        print_height_mm: ref.height_mm,
        ratio_decimal: r.decimal,
        ratio_display: r.display,
        ratio_source: "reference",
        sizing_confirmed: false,
      });
      if (ref.last_flag === "too_big" || ref.last_flag === "too_small") {
        const dir = ref.last_flag === "too_big" ? "too big" : "too small";
        setAutoNote(`Last job: flagged ${dir} at completion${ref.last_flag_note ? ` — ${ref.last_flag_note}` : ""}. Corrected size pre-filled.`);
      } else {
        setAutoNote(`Pre-filled from last confirmed job (${ref.confirmed_count} confirmed).`);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryTarget?.sku, primaryTarget?.sizeRange, artwork.placement_name]);

  // Editing a dimension re-opens a confirmed group as unconfirmed
  const handleDimChange = (field, rawValue) => {
    const value = rawValue === "" ? null : parseFloat(rawValue);
    const fields = { [field]: value, ratio_source: "manual" };
    if (locked && lockedRatioRef.current && value) {
      if (field === "print_width_mm") {
        fields.print_height_mm = Math.round((value / lockedRatioRef.current) * 10) / 10;
      } else {
        fields.print_width_mm = Math.round(value * lockedRatioRef.current * 10) / 10;
      }
    }
    if (artwork.sizing_confirmed) fields.sizing_confirmed = false;
    setAutoNote(null);
    onUpdateFields(fields);
  };

  const toggleLock = () => {
    if (!locked) {
      lockedRatioRef.current = ratio.decimal || null;
    }
    setLocked(!locked);
  };

  const applySuggestedTier = () => {
    if (suggestedTier) onUpdate("size_tier", suggestedTier);
  };

  const canConfirm = width > 0 && height > 0 && artwork.placement_name && keyTargets.length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) {
      toast.error("Add a placement, applied garment(s), and width/height before confirming.");
      return;
    }
    setConfirming(true);
    try {
      for (const t of keyTargets) {
        await confirmSizing({
          garment_sku: t.sku,
          garment_label: t.label,
          size_range: t.sizeRange,
          placement: artwork.placement_name,
          width_mm: parseFloat(width),
          height_mm: parseFloat(height),
          print_method: artwork.print_method || "",
          artwork_type: artwork.artwork_type || null,
        });
      }
      onUpdateFields({
        sizing_confirmed: true,
        sizing_confirmed_at: new Date().toISOString(),
        size_range: primaryTarget?.sizeRange || "",
      });
      toast.success(`Sizing confirmed & saved to library (${keyTargets.length} garment${keyTargets.length > 1 ? "s" : ""}).`);
    } catch (e) {
      toast.error("Could not save sizing reference.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="pt-3 border-t" data-testid={`print-sizing-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Ruler className="w-3.5 h-3.5 text-blue-500" />
          Print Sizing
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">Physical print dimensions (production spec). Independent of the Size Tier pricing band. Confirm to save into your sizing library for reuse.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        {artwork.sizing_confirmed && (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]" data-testid={`sizing-confirmed-badge-${index}`}>
            <CheckCircle2 className="w-3 h-3 mr-1" /> Confirmed
          </Badge>
        )}
      </div>

      {/* 1 & 2. Upload Artwork + Adjust Bounding Box / Apply Ratio */}
      <ArtworkUpload artwork={artwork} index={index} jobId={jobId} onUpdateFields={onUpdateFields} />

      {/* 3. Lock Area Scale — ratio + lock + area */}
      <div className="grid grid-cols-2 gap-3 items-end mt-4">
        {/* Ratio + lock */}
        <div className="space-y-1">
          <Label className="text-xs">Ratio (Lock Area Scale)</Label>
          <div className="flex items-center gap-2 h-9">
            {ratio.decimal ? (
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-sm font-medium" data-testid={`ratio-decimal-${index}`}>{ratio.decimal}</span>
                <Badge variant="secondary" className="text-[10px] font-mono" data-testid={`ratio-display-${index}`}>{ratio.display}</Badge>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={locked ? "default" : "outline"}
                    size="icon"
                    className="h-7 w-7 ml-auto"
                    onClick={toggleLock}
                    data-testid={`ratio-lock-${index}`}
                  >
                    {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{locked ? "Ratio locked — editing one dimension scales the other" : "Lock ratio to scale width & height together"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        {/* Area */}
        <div className="space-y-1">
          <Label className="text-xs">Area</Label>
          <div className="flex items-center gap-2 h-9">
            {area !== null ? (
              <span className="font-mono text-sm" data-testid={`print-area-${index}`}>{area} cm²</span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        </div>
      </div>

      {/* 4. Input Print Measurements — width + height */}
      <div className="grid grid-cols-2 gap-3 items-end mt-3">
        <div className="space-y-1">
          <Label className="text-xs">Print Width (mm)</Label>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="e.g. 250"
            value={width}
            onChange={(e) => handleDimChange("print_width_mm", e.target.value)}
            className="font-mono"
            data-testid={`print-width-${index}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Print Height (mm)</Label>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="e.g. 180"
            value={height}
            onChange={(e) => handleDimChange("print_height_mm", e.target.value)}
            className="font-mono"
            data-testid={`print-height-${index}`}
          />
        </div>
      </div>

      {/* Size tier suggestion (never auto-forced) */}
      {suggestedTier && artwork.size_tier !== suggestedTier && (
        <div className="mt-2 flex items-center gap-2 text-xs bg-blue-50 border border-blue-100 rounded p-2" data-testid={`tier-suggestion-${index}`}>
          <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          <span className="text-blue-700">Suggested Size Tier from area: <strong>{suggestedTier}</strong></span>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-700 ml-auto" onClick={applySuggestedTier} data-testid={`apply-tier-${index}`}>
            Apply
          </Button>
        </div>
      )}

      {/* Auto-fill note */}
      {autoNote && (
        <div className="mt-2 flex items-start gap-2 text-xs bg-amber-50 border border-amber-100 rounded p-2" data-testid={`autofill-note-${index}`}>
          <Info className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
          <span className="text-amber-700">{autoNote}</span>
        </div>
      )}

      {/* Reference key targets + Confirm */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-muted-foreground">
          {keyTargets.length === 0 ? (
            <span>Allocate this print to a garment to enable saving.</span>
          ) : (
            <span>
              Saves against:{" "}
              {keyTargets.map((t, i) => (
                <span key={i} className="font-mono">
                  {t.label}{t.sizeRange ? ` (${t.sizeRange})` : ""}{i < keyTargets.length - 1 ? ", " : ""}
                </span>
              ))}
            </span>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant={artwork.sizing_confirmed ? "outline" : "default"}
          className="h-8 text-xs"
          disabled={!canConfirm || confirming}
          onClick={handleConfirm}
          data-testid={`confirm-sizing-${index}`}
        >
          {confirming ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
          {artwork.sizing_confirmed ? "Re-confirm sizing" : "Confirm sizing"}
        </Button>
      </div>
    </div>
  );
}

export default PrintSizingSection;
